import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { NetworkStatus } from "@apollo/client";
import { Alert, Button, Dialog, DialogContent, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { AutomationStatusType } from "@/types/graphql/partner.generated";
import {
  MARKETING_ERROR_KEYS,
  marketingErrorKey,
  useAutomationEditorQuery,
  useAutomationLiveQuery,
} from "@/partner/entities/marketing";
import { DialogHeader } from "@/shared/components";
import { fromAutomation, type AutomationFormState } from "../model/graphEditor";
import { AutomationDialogLoadingContent } from "./AutomationDialogLoading";
import { AutomationEditorErrorBoundary } from "./AutomationEditorErrorBoundary";
import type { AutomationTab } from "../model/automationTabs";
import { livePollInterval, usePageVisible } from "../model/livePolling";
import {
  NO_OUTCOME,
  statusChanged,
  type AutomationDialogOutcome,
} from "../model/listRefresh";

interface LoadedAutomation {
  state: AutomationFormState;
  revision: number;
  /** Статус при открытии: список под диалогом перечитывается, если он сменился. */
  status: AutomationStatusType;
  /** Растёт с каждой «Загрузить последнюю версию»: новый ключ редактора. */
  key: number;
}

/**
 * Диалог автоматизации (?tab=edit&id=, у цепочки ещё ?tab=participants&id=), fullScreen, как диалог рассылки.
 * Грузится статически (в основной чанк кабинета входит только он; холст
 * React Flow тянет ленивый `AutomationEditor`, загрузку чанка и границу
 * ошибки держит `AutomationEditorErrorBoundary`). Пока автоматизация
 * работает, статус и счётчики перечитываются лёгким запросом раз в 30
 * секунд, пока вкладка браузера видна (livePolling.ts);
 * форма монтируется с первым ответом и опросом не затирается
 * (`initialState` берётся один раз).
 *
 * Загрузка отдельно показывает три состояния: спиннер, «не найдена»
 * (удалена или это другая платформа) и общая ошибка сети с повтором
 * (`refetch`), вместо того чтобы закрывать диалог снекбаром: партнёр
 * пришёл сюда по ссылке `?tab=edit&id=`, и молчаливое закрытие не
 * объясняет, что случилось.
 *
 * Конфликт версий (финальное ревью I2/I3): вместе с initialState один раз
 * берётся и revision, от которой редактор сохраняет. «Загрузить последнюю
 * версию» (onReload) перечитывает автоматизацию, берёт новые initialState
 * и revision и монтирует редактор заново (ключ loaded.key): история,
 * выделение и признак конфликта сбрасываются вместе с правками.
 *
 * Escape (финальное ревью I1) закрывает диалог так же, как крестик, с
 * вопросом о несохранённых правках; Escape на холсте до диалога не
 * доходит (GraphCanvas снимает им выделение).
 */
export const AutomationDialog: FC<{
  id: string;
  onClose: () => void;
  /**
   * Диалог ушёл со страницы любым путём (крестик, кнопка браузера «Назад»,
   * смена адреса): список решает по исходу, перечитываться ли (listRefresh,
   * ревью WP-C, M2).
   */
  onSettled?: (outcome: AutomationDialogOutcome) => void;
  /** Вкладка цепочки из ?tab= (edit или participants), см. AutomationTabs. */
  tab?: AutomationTab;
  onTabChange?: (tab: AutomationTab) => void;
}> = ({ id, onClose, onSettled, tab, onTabChange }) => {
  const { t } = useTranslation();
  const { data, error, loading, networkStatus, refetch } =
    useAutomationEditorQuery({
      variables: { id },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    });
  // Минор 1 ре-ревью: настоящее состояние перечитывания (не только опрос),
  // чтобы кнопка «Повторить» в палитре не слала второй запрос по второму
  // клику, пока первый ещё в пути.
  const refreshing = networkStatus === NetworkStatus.refetch;
  const automation = data?.automation;
  // Супер-ревью 4: опрос запущенной автоматизации лёгким запросом
  // (AutomationLive: статус, версия, действия, счётчики), его ответ
  // обновляет Automation:<id> в кеше, и AutomationEditorQuery отдаёт
  // редактору свежие значения без графа, правил и проверки графа. В
  // скрытой вкладке браузера опроса нет.
  const visible = usePageVisible();
  const pollInterval = livePollInterval({
    status: automation?.status,
    open: true,
    visible,
  });
  useAutomationLiveQuery({
    variables: { id },
    // Первое чтение из кеша (поля уже записал AutomationEditorQuery), сам
    // опрос Apollo всегда идёт в сеть.
    fetchPolicy: "cache-first",
    pollInterval,
    skip: pollInterval === 0,
  });

  const [loaded, setLoaded] = useState<LoadedAutomation | null>(null);
  if (automation && !loaded) {
    setLoaded({
      state: fromAutomation(automation),
      revision: automation.revision,
      status: automation.status,
      key: 0,
    });
  }
  const onReload = useCallback(async () => {
    const result = await refetch();
    const fresh = result.data?.automation;
    if (!fresh) return false;
    setLoaded((prev) => ({
      state: fromAutomation(fresh),
      revision: fresh.revision,
      status: prev?.status ?? fresh.status,
      key: (prev?.key ?? 0) + 1,
    }));
    return true;
  }, [refetch]);

  const dirtyRef = useRef(false);
  const onDirtyChange = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);
  const handleClose = () => {
    if (
      dirtyRef.current &&
      !window.confirm(t("shared.form.unsavedChangesWarning"))
    )
      return;
    onClose();
  };
  // Исход для списка считается на каждом рендере, а отдаётся при
  // размонтировании: закрытие «Назад» меняет адрес мимо handleClose.
  const outcome: AutomationDialogOutcome = loaded
    ? {
        statusChanged: statusChanged(loaded.status, automation?.status ?? null),
      }
    : NO_OUTCOME;
  const outcomeRef = useRef(outcome);
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    outcomeRef.current = outcome;
    onSettledRef.current = onSettled;
  });
  useEffect(() => () => onSettledRef.current?.(outcomeRef.current), []);

  const notFound =
    Boolean(error) &&
    !automation &&
    marketingErrorKey(error) === MARKETING_ERROR_KEYS.AUTOMATION_NOT_FOUND;
  const loadFailed = Boolean(error) && !automation && !notFound;

  let content = <AutomationDialogLoadingContent onClose={handleClose} />;
  if (automation && loaded) {
    content = (
      <AutomationEditorErrorBoundary
        key={loaded.key}
        automation={automation}
        initialState={loaded.state}
        initialRevision={loaded.revision}
        onReload={onReload}
        onClose={handleClose}
        onDirtyChange={onDirtyChange}
        onNeedsRefresh={() => void refetch()}
        refreshing={refreshing}
        tab={tab}
        onTabChange={onTabChange}
      />
    );
  } else if (notFound || loadFailed) {
    content = (
      <>
        <DialogHeader
          title={t("automations.editor.title")}
          onClose={handleClose}
        />
        <DialogContent>
          <Stack spacing={2} alignItems="flex-start">
            <Alert severity={notFound ? "warning" : "error"}>
              {t(marketingErrorKey(error))}
            </Alert>
            {!notFound && (
              <Button
                variant="outlined"
                // Review M5: клик на повторной сетевой ошибке не должен
                // оставлять непойманный отказ промиса, состояние ошибки уже
                // само перерисует этот же экран.
                onClick={() => void refetch().catch(() => undefined)}
                disabled={loading}
              >
                {t("automations.list.retry")}
              </Button>
            )}
          </Stack>
        </DialogContent>
      </>
    );
  }

  return (
    <Dialog open fullScreen disableEscapeKeyDown onClose={handleClose}>
      {content}
    </Dialog>
  );
};
